/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { getInitialState, VISUAL_SLICE_NAME } from './constants';
import { FilterBarState, FilterItem, Selection, SetFilterBarStatePayload } from './types';
import * as thunks from './thunks';

const filterKeys: (Exclude<keyof FilterBarState, 'search' | 'init' | 'project' | 'userUpdated'>)[] = ['os', 'browser', 'device', 'status'];
const nonSearchKeys: (Exclude<keyof SetFilterBarStatePayload, 'status' | 'init' | 'project'>)[] = ['os', 'browser', 'device'];

export const filterBarSlice = createSlice({
  name: VISUAL_SLICE_NAME,
  initialState: getInitialState(),
  reducers: {
    setSearchValue: (state, action: PayloadAction<string>) => {
      state.userUpdated = true;
      state.search.inputValue = action.payload;
    },
    setFilterValue: (state, action: PayloadAction<{
      key: typeof filterKeys[number],
      value: Selection,
    }>) => {
      const {
        payload: {
          key,
          value,
        },
      } = action;

      if (value === 'all') {
        state[key].value = state[key].items.map((item: FilterItem) => item.key);
      } else {
        state[key].value = Array.from(value) as string[];
      }

      state.userUpdated = true;
    },
    // All / global
    clearFilters: (state) => {
      const initialState = getInitialState();

      state.search = initialState.search;
      filterKeys.forEach((key) => {
        state[key].value = initialState[key].value;
      });
    },
    resetFilters: (state) => {
      const initialState = getInitialState();

      // Assign / overwrite all values of state with their initial value
      Object.assign(state, initialState);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(thunks.updateBuildDetailsSearch.fulfilled, (state, action) => {
        state.search.searchValue = action.payload;
      })
      .addCase(thunks.initFilters.fulfilled, (state, action) => {
        const { params, payload, project } = action.payload;

        state.project = project ?? getInitialState().project;

        nonSearchKeys.forEach((key) => {
          state[key].items = payload[key];
          state[key].value = [];
        });

        [...filterKeys, 'groupBy'].forEach((key) => {
          const values = params[key];
          if (values) {
            state[key].value = values
              .filter((value) => state[key].items.findIndex((item) => item.key === value) !== -1);
          }
        });

        if (params.search) {
          state.search.inputValue = params.search;
          state.search.searchValue = params.search;
        }

        state.init = 'succeeded';
      });
  },
});
